import { Component, OnInit, OnDestroy } from '@angular/core';
import { ImpactQuestionsService } from 'src/app/services/impact-questions/impact-questions.service';
import { CycleService } from 'src/app/services/cycle/cycle.service';
import { Params, Router, ActivatedRoute, RouterModule } from '@angular/router';
import { Impact } from 'src/app/models/data/impact.model';
import { FormBuilder, FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Observable, take } from 'rxjs';
import { AutoSaveService } from 'src/app/services/auto-save/auto-save.service';

// previously removed for multi-question support
import { PageService } from 'src/app/services/page/page.service';
import { DropdownService } from 'src/app/services/dropdown/dropdown.service';
import { Page } from 'src/app/models/data/page.model';

import { CommonModule } from '@angular/common';
import { LoadingSpinnerComponent } from '../../../shared/loading-spinner/loading-spinner.component';

// multi-question support
// import { QuestionsService } from 'src/app/services/stat-questions/stat-questions.service';
// import { Questions } from 'src/app/models/data/stat-questions.model';

@Component({
  selector: 'app-impact-questions-detail',
  templateUrl: './impact-questions-detail.component.html',
  styleUrls: ['./impact-questions-detail.component.css'],
  imports: [CommonModule, RouterModule, LoadingSpinnerComponent, ReactiveFormsModule],
})
export class ImpactQuestionsDetailComponent implements OnInit, OnDestroy {
  id!: number;

  // multi-question support
  // questions: Questions[] = [];
  // questions$!: Observable<Questions[]>;

  impact$!: Observable<Impact>;
  impactForm!: FormGroup;
  isLoading = false;
  header!: string;
  error!: string | null;
  pageHeader: string = 'Impact Information';
  q: string = 'Test <B>This!</B> ';
  instructions = 'If a question was not applicable to your ministry work in the last six months, please enter NA';

  constructor(
    private fb: FormBuilder,
    private impactService: ImpactQuestionsService,
    private route: ActivatedRoute,
    private router: Router,
    // previously removed for multi-question support
    private dropdownService: DropdownService,
    private pageService: PageService,
    private autoSaveService: AutoSaveService,
    private cycleService: CycleService,
    // multi-question support
    // private questionService: QuestionsService,
  ) { }

  ngOnInit() {
    this.impactForm = this.fb.group({
      answer: ['', [Validators.required]]
    });

    // Fetch cycle banner from API and update pageHeader
    this.cycleService.getCycleBanner().subscribe({
      next: (banner) => {
        console.log('💥 IMPACT QUESTION: Cycle banner received:', banner);
        if (banner) {
          this.pageHeader = `Impact Information ${banner}`;
        }
      },
      error: (error) => {
        console.error('💥 IMPACT QUESTION: Error getting cycle banner:', error);
        // Keep default pageHeader on error
      }
    });

    // Get current route path
    const currentPath = this.router.url.substring(1); // Remove leading slash

    // Find page data from PageService
    this.pageService.pages.pipe(take(1)).subscribe((pages: Page[]) => {
      const currentPage = pages.find(p => p.Route === currentPath);
      if (currentPage) {
        this.header = currentPage.Title;
        // Impact pages are PageNumber 9-27, map to impact IDs 1-19
        this.id = currentPage.PageNumber - 8;
        this.getData(this.id);

        this.isLoading = true;

        // multi-question support
        // this.questionService.getPage(this.id).subscribe((res) => {
        //   this.isLoading = false;
        // });

        // this.questions$ = this.questionService.getSection();

        // this.questions$.subscribe((questions: Questions[]) => {
        //   this.questions = questions;
        // });
      }
    });
  }

  getData(id: number) {
    this.error = null;
    this.impactService.getImpactQuestion(id).subscribe((res) => {
      this.isLoading = false;
      this.q = 'Got <B>It!</B> ';
    });
    this.impact$ = this.impactService.getImpactObs();

    // Subscribe to impact data to populate form
    this.impact$.subscribe(impact => {
      if (impact) {
        this.impactForm.patchValue({
          answer: impact.answer || ''
        });

        // Initialize auto-save for the answer field
        this.initializeAutoSave();
      }
    });
  }

  onForward() {
    if (this.impactForm.invalid) {
      this.error =
        'Please fill in the field. If the question does not apply to your work, please enter NA';
      return;
    }

    const answer = this.impactForm.value.answer;
    this.impactService.updateImpactAnswer(answer, this.id);

    this.impactForm.reset();
    this.error = null;

    // Get next page from database
    this.pageService.pages.pipe(take(1)).subscribe((pages: Page[]) => {
      const sortedPages = pages.sort((a, b) => a.PageNumber - b.PageNumber);
      const currentPageIndex = sortedPages.findIndex(p => p.PageNumber === this.id + 8); // Convert back to PageNumber

      if (currentPageIndex >= 0 && currentPageIndex < sortedPages.length - 1) {
        const nextPage = sortedPages[currentPageIndex + 1];
        const nextRoute = nextPage.Route.startsWith('/') ? nextPage.Route : `/${nextPage.Route}`;
        this.router.navigate([nextRoute]);
      } else {
        // If this is the last page, navigate to photo upload
        this.router.navigate(['/photo-upload']);
      }
    });
  }

  qq() {
    return this.impactService.qq(); //
    //    return this.q; //
  }

  onClose() {
    this.error = null;
  }

  private initializeAutoSave() {
    const fieldId = `impact-question-${this.id}`;
    const answerControl = this.impactForm.get('answer') as FormControl;

    const saveFunction = async (value: any) => {
      // Allow saving even if field is empty (to support clearing answers)
      // Form validation still handles required field validation on submit
      // Convert null/undefined to empty string for consistency with backend
      const valueToSave = value === null || value === undefined ? '' : value;
      return this.impactService.updateImpactAnswer(valueToSave, this.id, false);
    };

    this.autoSaveService.initializeAutoSave(fieldId, saveFunction, {
      debounceMs: 500,
      validateField: true
    });
  }

  onFieldBlur() {
    const fieldId = `impact-question-${this.id}`;
    const answer = this.impactForm.get('answer')?.value;

    // Allow saving empty strings, null, or undefined to support clearing answers
    // The backend accepts empty strings for QA endpoints
    // Convert null/undefined to empty string for consistency
    const valueToSave = answer === null || answer === undefined ? '' : answer;
    this.autoSaveService.triggerSave(fieldId, valueToSave);
  }

  ngOnDestroy() {
    const fieldId = `impact-question-${this.id}`;
    this.autoSaveService.cleanup(fieldId);
  }
}
